import { Add } from '@mui/icons-material';
import { Box, Menu, MenuItem, Select } from '@mui/material';
import React, { useContext, useState } from 'react';
import { DisableContext } from '../../contexts/DisableContextProvider';
import { useApproveToggleEmployeeTimeOffs } from '../../services/react-query/employee_time_offs/useApproveToggleEmployeeTimeOffs';
import { useGetAllEmployeeTimeOffs } from '../../services/react-query/employee_time_offs/useGetAllEmployeeTimeOffs';
import { useGetEmployeeById } from '../../services/react-query/employees';
import { queryClient } from '../../services/react-query/queryClient';
import { queryKeys } from '../../services/react-query/queryKeys';
import { EmployeeTimeOff, EmployeeTimeOffMode } from '../../types/EmployeeTimeOff';
import { UserSettingsCollapseKeys } from '../../types/User';
import { Loading } from '../../uikit';
import ResponsiveButton from '../../uikit/buttons/ResponsiveButton';
import AccordionSection from '../AccordionSection';
import TimeOffModal from '../common/employees/TimeOffModal';
import EmployeeAvailabilityTable from './EmployeeAvailabilityTable';
import { FEATURE_FLAGS, useGetFeatureFlags } from '../../services/react-query/users/useGetFeatureFlags';

enum Filter {
  ACTIVE = 'active',
  PAST = 'past',
}

interface Props {
  employeeUuid: string;
}

export default function EmployeeAvailabilitySection({ employeeUuid }: Props) {
  const [filter, setFilter] = useState<Filter>(Filter.ACTIVE);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [modalType, setModalType] = useState<EmployeeTimeOffMode | null>(null);
  const [editingUuid, setEditingUuid] = useState<string>();
  const { disabled } = useContext(DisableContext);

  const { data: currentEmployeeData } = useGetEmployeeById({ uuid: 'current' });
  const isCurrentEmployee = currentEmployeeData?.uuid === employeeUuid;

  const { data: featureFlags } = useGetFeatureFlags();
  const isFeatureEnabled = featureFlags?.[FEATURE_FLAGS.employeeAvailability];

  const canManageAll = !disabled;
  const canManageOwnTimeOff = disabled && isCurrentEmployee;
  const showAddButton = canManageAll || canManageOwnTimeOff;

  const params = {
    per_page: 0,
    filters: {
      employee_uuid: [employeeUuid],
      status: filter,
    },
  };
  const { data: employeeTimeOffsData, isLoading } = useGetAllEmployeeTimeOffs({ params, enabled: isFeatureEnabled });
  const { mutate: approveToggleEmployeeTimeOffs } = useApproveToggleEmployeeTimeOffs(() => {
    queryClient.invalidateQueries({
      queryKey: [queryKeys.allEmployeeTimeOffs, params],
    });
  });

  if (!isFeatureEnabled) {
    return null;
  }

  const rows = employeeTimeOffsData?.data || [];

  const closeModal = () => {
    setModalType(null);
    setEditingUuid(undefined);
    queryClient.invalidateQueries({
      queryKey: [queryKeys.allEmployeeTimeOffs, params],
    });
  };

  const handleEdit = (uuid: string, mode: EmployeeTimeOffMode) => {
    setEditingUuid(uuid);
    setModalType(mode);
  };

  const handleAddMenuClick = (mode: EmployeeTimeOffMode) => {
    setAnchorEl(null);
    setEditingUuid('new');
    setModalType(mode);

    queryClient.removeQueries({
      queryKey: [queryKeys.allEmployeeTimeOffs, 'new'],
    });
  };

  const handleApproveToggle = (employeeTimeOff: EmployeeTimeOff) => {
    approveToggleEmployeeTimeOffs({
      uuids: [employeeTimeOff.uuid],
      approved: !employeeTimeOff.approved,
    });
  };

  return (
    <>
      <AccordionSection
        title="Availability"
        collapseId={UserSettingsCollapseKeys.EMPLOYEE_ITEM_AVAILABILITY}
        hasData={rows.length > 0}
        scrollable
        headerElements={
          <Box display="flex" gap={2}>
            <Select size="small" value={filter} onChange={(e) => setFilter(e.target.value as Filter)}>
              <MenuItem value={Filter.ACTIVE}>Active</MenuItem>

              <MenuItem value={Filter.PAST}>Past</MenuItem>
            </Select>

            <ResponsiveButton disabled={!showAddButton} startIcon={<Add />} variant="outlined" onClick={(e) => setAnchorEl(e.currentTarget)}>
              Add
            </ResponsiveButton>
          </Box>
        }
      >
        {isLoading ? (
          <Loading />
        ) : (
          <EmployeeAvailabilityTable
            rows={rows}
            onEdit={handleEdit}
            onApproveToggle={handleApproveToggle}
            canManageAll={canManageAll}
            canManageOwnTimeOff={canManageOwnTimeOff}
          />
        )}
      </AccordionSection>

      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => handleAddMenuClick(EmployeeTimeOffMode.FULL)}>Time Off</MenuItem>

        {canManageAll && <MenuItem onClick={() => handleAddMenuClick(EmployeeTimeOffMode.WEEKLY)}>Schedule</MenuItem>}
      </Menu>

      {modalType && <TimeOffModal open uuid={editingUuid} employeeUuid={employeeUuid} mode={modalType} onClose={closeModal} />}
    </>
  );
}
